import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import {
  Route,
  Switch,
  withRouter
} from 'react-router-dom'
// Local
import {
  PAGES,
  shouldRenderReskin2025
} from './constant'
import { isEmbeddedView } from './component/common/currentUserUtils'
import PrivateRoute from './component/common/privateRoute'
import AutoLogin from './component/login/autoLogin'
import Authenticated from './component/authenticated'
import EmptyScope from './component/fallback/emptyScope'
import Encounter from './component/encounter/encounter'
import Feedback from './component/feedback'
import ForgotPassword from './component/forgotpassword'
import Login from './component/login'
import NlpConditionEvidence from './component/encounter/condition/nlpConditionEvidence'
import NlpConditionStandalone from './component/evidenceviewer/oneCondition'
import Register from './component/register'
import Reset from './component/reset'
// Styling
import './css/main-stylus.css'
import './app.css'
import VimLogin from "./component/login/vimLogin";
import Vim from "./component/vim";

type RootState = {
  user: {
    isAuthenticated: boolean
  }
}

type AppProps = {
  isAuthenticated: boolean
  location: {
    pathname: string
  }
}

/**
 * Provides the top level app container and routing for the app
 *
 * This top-level routing is handling 2 scenarios: The user is not logged in;
 * or the user is logged in via an external source and will pass through the
 * auto-login routing as part of a launch or embedded integration.
 *
 * Once the user is signed in the request passes through a second phase of
 * routing in the Authenticated component: authenticated/index.js
 *
 * The PrivateRoute components allow the app to render without visual headers.
 * Again, this is used in launch and embedded integrations
 *
 * See also: login/autoLogin.js and action/user.js: autoLogin(params)
 */
export function App(props: AppProps): JSX.Element {
  const {
    isAuthenticated,
    location
  } = props

  useEffect(() => {
    if (shouldRenderReskin2025) {
      document.body.classList.add('reskin')
    } else {
      document.body.classList.remove('reskin')
    }
  }, [])

  return (
    <div className="App">
      <Switch>
        <Route
          exact
          path={ PAGES.LOGIN }
          component={ Login }
        />
        <Route
          exact
          path={ PAGES.AUTO_LOGIN }
          component={ AutoLogin }
        />
        <Route
          exact
          path={ PAGES.VIM_LOGIN }
          component={ VimLogin }
        />
        <Route
          exact
          path={ PAGES.VIM_COMING_SOON }
          component={ Vim }
        />
        <Route
          exact
          path={ PAGES.PASSWORD_RESET }
          component={ Reset }
        />
        <Route
          exact
          path={ PAGES.REGISTER }
          component={ Register }
        />
        <Route
          exact
          path={ PAGES.FORGOT_PASSWORD }
          component={ ForgotPassword }
        />
        <PrivateRoute
          path={ `${PAGES.SMART_ENCOUNTER_DETAIL}:id/conditions/:conditions` }
          component={ Encounter }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.SMART_ENCOUNTER_DETAIL}:id` }
          component={ Encounter }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.EMBEDDED_ENCOUNTER_DETAIL}:id/conditions/:conditions` }
          component={ Encounter }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.EMBEDDED_ENCOUNTER_DETAIL}:id` }
          component={ Encounter }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.EMBEDDED_MDP_NLP_CONDITION }:mdpSourceId/source/:mdpSourceType` }
          component={ NlpConditionStandalone }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.SMART_MDP_NLP_CONDITION }:mdpSourceId/source/:mdpSourceType` }
          component={ NlpConditionStandalone }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.EMBEDDED_NLP_CONDITION}:encounterConditionId/encounter/:encounterId` }
          component={ NlpConditionEvidence }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.SMART_NLP_CONDITION}:encounterConditionId/encounter/:encounterId` }
          component={ NlpConditionEvidence }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path={ `${PAGES.EMPTY_SCOPE}` }
          component={ EmptyScope }
          authenticated={ isAuthenticated }
        />
        <PrivateRoute
          path="/auth"
          component={ Authenticated }
          authenticated={ isAuthenticated }
        />
        <Route component={ Login } />
      </Switch>
      {
        !isEmbeddedView(location.pathname) &&
        <Feedback />
      }
    </div>
  )
}

export default withRouter(
  connect(
    (state: RootState) => ({
      isAuthenticated: state.user.isAuthenticated
    }),
    {}
  )(App) as any
)
